import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <div className="w-10 h-10 rounded-lg bg-amber-950 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-amber-500" fill="currentColor">
                <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
              </svg>
            </div>
            <div>
              <span className="text-xl font-bold text-amber-950 font-serif">Buy Fusion</span>
              <p className="text-xs text-stone-500 -mt-1">Premium Flooring Co.</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-amber-600'
                    : 'text-stone-600 hover:text-amber-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+18888791244"
              className="flex items-center gap-2 text-green-700 font-semibold text-sm"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              +1-888-879-1244
            </a>
            <Link to="/contact">
              <Button
                className="bg-green-700 hover:bg-green-800 text-white rounded-full px-6"
                data-testid="header-cta-btn"
              >
                Get Free Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-stone-700" />
            ) : (
              <Menu className="w-6 h-6 text-stone-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-stone-200">
          <nav className="flex flex-col px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setIsMenuOpen(false)}
                className={`py-3 px-4 text-base font-medium rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-stone-600 hover:bg-stone-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="tel:+18888791244"
              className="mt-4 py-4 px-4 bg-green-700 text-white rounded-full text-center font-semibold flex items-center justify-center gap-2"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call Now: +1-888-879-1244
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
